import React from 'react';

export interface IOrbitMarkWhiteProps {
  /** Drawn slot width. 39.03 on the launcher tile. */
  width?: number | string;
  /** Drawn slot height. 39.85 on the launcher tile. */
  height?: number | string;
  className?: string;
  /**
   * Ink for all five vectors. A literal, not a `var()` reference: an SVG `fill` attribute is not a
   * CSS declaration and does not substitute custom properties.
   */
  fill?: string;
}

/**
 * The Orbit mark as the designer exported it, WHITE: Figma `5017:6364` "Orbit Icon 3", five vectors
 * in a 40 x 40 box. Drawn on the terracotta launcher tile (`5017:10253` collapsed, `5017:6364`
 * expanded) at 39.03 x 39.85. The assistant avatar wears `OrbitMarkReply` (`5199:10088`) instead.
 *
 * The fill defaults to the drawn `#FFFFFF`, as the export carries it per path; the assistant
 * avatar passes the light palette's `avatarAssistantMark` under `perx_3_redesign`. `OrbitMark` is the sibling terracotta mark for the open
 * panel's header. The exported `clipPath` is dropped - its rect is 39.03 x 39.85 from the origin
 * and every path already falls inside it, so it clipped nothing and only risked a duplicate
 * element id wherever two marks render at once.
 */
const OrbitMarkWhite: React.FC<IOrbitMarkWhiteProps> = ({
  width = 39.03,
  height = 39.85,
  className,
  fill = '#FFFFFF'
}) => (
  <svg
    className={className}
    width={width}
    height={height}
    viewBox="0 0 40 40"
    preserveAspectRatio="xMidYMid meet"
    fill="none"
    focusable="false"
    aria-hidden="true"
    data-testid="orbit-mark-white"
  >
    <path
      d="M32.2822 0C33.7477 2.14126 36.7145 2.72768 38.9221 1.31795L38.948 1.30127C36.7377 2.73226 36.1388 5.63494 37.6101 7.78459C36.1388 5.63494 33.1544 5.05233 30.9443 6.48332C33.1544 5.05233 33.7534 2.14965 32.2822 0Z"
      fill={fill}
    />
    <path
      d="M36.9158 3.28516C25.5939 9.29264 8.63009 20.3542 6.13324 28.7011C5.66419 30.2691 5.08738 31.9436 5.08737 33.5776C5.08737 35.9658 7.15053 37.902 9.69568 37.902C10.4257 37.902 11.1161 37.7426 11.7295 37.459C12.0243 37.3228 12.3579 37.2711 12.671 37.3597C13.7576 37.6674 13.7649 39.0634 12.6575 39.2886C9.04262 40.0238 4.44001 40.3114 1.99726 38.3535C-2.21831 34.9746 1.23487 18.7888 36.9158 3.28516Z"
      fill={fill}
    />
    <path
      d="M28.0973 5.02119C28.7831 4.04245 28.5327 2.68209 27.4477 2.15391C24.9426 0.934487 22.569 0.685174 19.3899 0.705183C15.4261 0.730134 11.5639 1.92852 8.31744 4.14089C5.07097 6.35325 2.59414 9.4747 1.21651 13.0899C0.190279 15.7829 -0.189749 18.6548 0.0881423 21.4889C0.232049 22.9565 1.89637 23.6452 3.19804 22.9058C4.07864 22.4057 4.48353 21.3949 4.35569 20.4053C4.09546 18.3907 4.48151 16.8028 5.33117 14.5732C6.39905 11.7709 8.31896 9.35126 10.8355 7.63635C13.352 5.92142 16.3457 4.99249 19.4184 4.97315C21.6837 4.95889 23.2792 5.0287 24.9787 5.74085C26.0816 6.20303 27.4183 5.99024 28.0973 5.02119Z"
      fill={fill}
    />
    <path
      d="M31.7764 7.76238C32.6286 6.93346 34.0149 6.92659 34.7674 7.84344C36.043 9.39712 37.0624 11.1396 37.7846 13.0088C38.7631 15.5408 39.1739 18.2469 38.9895 20.9459C38.805 23.6449 38.0296 26.2744 36.7152 28.6582C35.4009 31.0419 33.5779 33.125 31.3683 34.7676C29.1589 36.4104 26.614 37.5748 23.904 38.183C21.1942 38.7913 18.382 38.829 15.6559 38.294C12.9298 37.7589 10.3527 36.6635 8.09732 35.0808C6.4425 33.9197 4.98891 32.5159 3.78638 30.9233C3.06359 29.9662 3.43883 28.6387 4.49529 28.0504C5.54207 27.4676 6.86091 27.8345 7.60681 28.7606C8.48081 29.8457 9.50953 30.8089 10.6648 31.6197C12.413 32.8464 14.4107 33.6956 16.5238 34.1103C18.6371 34.525 20.8169 34.4957 22.9175 34.0243C25.0181 33.5528 26.9907 32.6502 28.7034 31.3768C30.4161 30.1034 31.8293 28.4889 32.848 26.641C33.8669 24.7933 34.4679 22.7549 34.6108 20.6629C34.7538 18.5708 34.4355 16.4732 33.677 14.5104C33.1788 13.221 32.4982 12.0095 31.6577 10.9096C30.93 9.95761 30.9151 8.60019 31.7764 7.76238Z"
      fill={fill}
    />
    <path
      d="M6.42353 20.6108C9.22458 17.964 7.53624 15.1879 4.18947 17.1331L2.26222 18.1541C0.869598 18.892 -0.0643548 20.3048 0.0717368 21.8505C0.0851779 22.0032 0.100317 22.1509 0.116988 22.2941C0.364575 24.4191 2.4918 24.5594 3.99405 23.0078C4.78376 22.1921 5.59697 21.3918 6.42353 20.6108Z"
      fill={fill}
    />
  </svg>
);

export default OrbitMarkWhite;
